import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './mini-weekly-calendar.module.scss';
import { MonthNamesNum, WeekDaysArray } from 'shared/consts/calendar.consts';
import { ModalWindow } from 'shared/ui';
import { LeftArrowSvg, RightArrowSvg } from 'shared/svg';
import { RoundTriangle } from 'shared/ui/triangle/round-triangle';
import { DayInWeekEnum } from 'shared/api/booking/interfaces/calendar-week-info-type.interface';
import { IWeek } from 'shared/hooks';
import { BookingApi } from 'shared/api';
import * as dateFns from 'date-fns';
import { dayCalendarStore } from 'entities/index';
import { MiniCalendar } from 'features/mini-calendar/ui/mini-calendar';
import { observer } from 'mobx-react-lite';

interface IMiniWeeklyCalendarProps {
  date: Date;
  dateChangeHandler: (date: Date) => void;
  week: IWeek[];
  changeWeekHandler: (change: number) => void;
  microSlotDuration: number;
  setMicroSlotDuration: (duration: number) => void;
  isMobile?: boolean;
}

export const MiniWeeklyCalendar: React.FC<IMiniWeeklyCalendarProps> = observer(
  ({
    date,
    dateChangeHandler,
    week,
    changeWeekHandler,
    microSlotDuration,
    setMicroSlotDuration,
    isMobile = false,
  }) => {
    const [newDate, setNewDate] = useState<Date>(date);
    const [month, setMonth] = useState<number>(date.getMonth());

    const [currentWeek, setCurrentWeek] = useState<IWeek[]>(week);
    const [nextWeek, setNextWeek] = useState<IWeek[] | null>(null);
    const [prevWeek, setPrevWeek] = useState<IWeek[] | null>(null);

    const [animationDirection, setAnimationDirection] = useState<
      'left' | 'right' | null
    >(null);
    const [isAnimating, setIsAnimating] = useState(false);

    const [, setDragOffset] = useState(0);

    const [isMicroSlotOpen, setIsMicroSlotOpen] = useState(false);
    const [isLegendOpen, setIsLegendOpen] = useState(false);
    const [isMonthCalendarOpen, setIsMonthCalendarOpen] = useState(false);
    const microSlotRef = useRef<HTMLDivElement>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef<number>(0);
    const touchStartY = useRef<number>(0);
    const isDragging = useRef<boolean>(false);
    const currentDragStart = useRef<number>(0);
    const isHandlingTransition = useRef(false);
    const isDayClickRef = useRef(false);

    const fetchWeek = useCallback(
      async (targetWeekNum: number, targetDate: Date): Promise<IWeek[]> => {
        const pointId = localStorage.getItem('pointId');
        if (!pointId) return [];

        const year = dateFns.getISOWeekYear(targetDate);
        const startOfYear = dateFns.startOfISOWeekYear(targetDate);
        const dateInWeek = dateFns.setISOWeek(startOfYear, targetWeekNum);
        const startOfWeekDate = dateFns.startOfISOWeek(dateInWeek);

        try {
          const weekData = await BookingApi.getCalendarWeekInfo({
            year,
            weekNumber: targetWeekNum,
            pointId,
          });

          return Array.from({ length: 7 }).map((_, index) => {
            const day = dateFns.addDays(startOfWeekDate, index);
            const dayNumber = Number(dateFns.format(day, 'd'));
            return {
              date: day,
              dayNumber,
              type: weekData[dayNumber] || DayInWeekEnum.WORK_DAY,
            };
          });
        } catch {
          return [];
        }
      },
      []
    );

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          microSlotRef.current &&
          !microSlotRef.current.contains(event.target as Node)
        ) {
          setIsMicroSlotOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    useEffect(() => {
      if (isDayClickRef.current) {
        isDayClickRef.current = false;
        return;
      }

      if (date.getTime() !== newDate.getTime()) {
        setNewDate(date);
        setMonth(date.getMonth());
      }

      const currentWeekNum = currentWeek[0]?.date
        ? dateFns.getISOWeek(currentWeek[0].date)
        : null;
      const propWeekNum = week[0]?.date
        ? dateFns.getISOWeek(week[0].date)
        : null;

      if (currentWeekNum !== propWeekNum) {
        setCurrentWeek(week);
      }

      const loadAllWeeks = async () => {
        if (!week[0]?.date) return;
        const weekNum = dateFns.getISOWeek(week[0].date);
        const currentDate = week[0].date;

        const [prev, current, next] = await Promise.all([
          fetchWeek(weekNum - 1, currentDate),
          fetchWeek(weekNum, currentDate),
          fetchWeek(weekNum + 1, currentDate),
        ]);

        setPrevWeek(prev);
        setCurrentWeek(current);
        setNextWeek(next);
      };

      loadAllWeeks();
    }, [date, week, newDate]);

    const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
      if (isAnimating) return;

      const clientX =
        'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const clientY =
        'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

      touchStartX.current = clientX;
      touchStartY.current = clientY;
      currentDragStart.current = 0;
      isDragging.current = true;
      setDragOffset(0);
    };

    const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
      if (!isDragging.current || isAnimating) return;

      const clientX =
        'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const clientY =
        'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

      const deltaX = clientX - touchStartX.current;
      const deltaY = clientY - touchStartY.current;

      if (Math.abs(deltaY) > Math.abs(deltaX)) return;

      e.preventDefault();
      currentDragStart.current = deltaX;
      setDragOffset(deltaX);
    };

    const handleTouchEnd = () => {
      if (!isDragging.current) return;

      isDragging.current = false;

      if (!containerRef.current) {
        setDragOffset(0);
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      const threshold = containerWidth * 0.3;

      if (currentDragStart.current > threshold) {
        animateToWeek('prev');
      } else if (currentDragStart.current < -threshold) {
        animateToWeek('next');
      } else {
        setDragOffset(0);
      }
    };

    const animateToWeek = async (direction: 'prev' | 'next') => {
      if (isAnimating) return;

      const targetWeek = direction === 'next' ? nextWeek : prevWeek;

      if (!targetWeek) {
        const weekNum = dateFns.getISOWeek(currentWeek[0]?.date || date);
        const targetDate = currentWeek[0]?.date || date;
        const newWeekNum = direction === 'next' ? weekNum + 1 : weekNum - 1;
        const fetchedWeek = await fetchWeek(newWeekNum, targetDate);

        if (direction === 'next') {
          setNextWeek(fetchedWeek);
        } else {
          setPrevWeek(fetchedWeek);
        }
      }

      setAnimationDirection(direction === 'next' ? 'right' : 'left');
      setIsAnimating(true);
    };

    const handleTransitionEnd = useCallback(() => {
      if (!isAnimating) return;

      isHandlingTransition.current = true;

      if (animationDirection === 'right') {
        const newCurrentWeek = nextWeek || currentWeek;
        const newCurrentDate = newCurrentWeek[0]?.date || date;
        const nextWeekNum = dateFns.getISOWeek(newCurrentDate) + 1;

        setCurrentWeek(newCurrentWeek);
        setPrevWeek(currentWeek);

        fetchWeek(nextWeekNum, newCurrentDate).then((newNextWeek) => {
          setNextWeek(newNextWeek);
        });
      } else if (animationDirection === 'left') {
        const newCurrentWeek = prevWeek || currentWeek;
        const newCurrentDate = newCurrentWeek[0]?.date || date;
        const prevWeekNum = dateFns.getISOWeek(newCurrentDate) - 1;

        setCurrentWeek(newCurrentWeek);
        setNextWeek(currentWeek);

        fetchWeek(prevWeekNum, newCurrentDate).then((newPrevWeek) => {
          setPrevWeek(newPrevWeek);
        });
      }

      setIsAnimating(false);
      setDragOffset(0);
      setAnimationDirection(null);

      setTimeout(() => {
        isHandlingTransition.current = false;
      }, 0);
    }, [
      isAnimating,
      animationDirection,
      nextWeek,
      prevWeek,
      currentWeek,
      changeWeekHandler,
      date,
      fetchWeek,
    ]);

    const handleArrowClick = (direction: number) => {
      if (isAnimating) return;

      if (direction === -1) {
        animateToWeek('prev');
      } else {
        animateToWeek('next');
      }
    };

    const goToToday = () => {
      const today = new Date();
      setNewDate(today);
      setMonth(today.getMonth());
      dateChangeHandler(today);
      dayCalendarStore.setSelectedDate({
        year: today.getFullYear(),
        month: today.getMonth(),
        day: today.getDate(),
      });
    };

    const dayClickHandler = (day: IWeek) => {
      isDayClickRef.current = true;
      dateChangeHandler(day.date);
      setNewDate(day.date);
      setMonth(day.date.getMonth());
      dayCalendarStore.setSelectedDate({
        year: day.date.getFullYear(),
        month: day.date.getMonth(),
        day: day.date.getDate(),
      });

      const clickedWeekNum = dateFns.getISOWeek(day.date);
      const prevWeekNum = prevWeek?.[0]?.date
        ? dateFns.getISOWeek(prevWeek[0].date)
        : null;
      const nextWeekNum = nextWeek?.[0]?.date
        ? dateFns.getISOWeek(nextWeek[0].date)
        : null;

      if (prevWeek && prevWeekNum === clickedWeekNum) {
        setCurrentWeek(prevWeek);
      } else if (nextWeek && nextWeekNum === clickedWeekNum) {
        setCurrentWeek(nextWeek);
      }
    };

    const renderWeekDays = (days: IWeek[]) => {
      return days.map((day) => {
        let dayClassName;
        switch (day.type) {
          case DayInWeekEnum.DAY_OFF:
            dayClassName = styles.days__day_off;
            break;
        }
        if (
          newDate.getDate() === day.date.getDate() &&
          dateFns.isSameDay(day.date, newDate)
        ) {
          dayClassName = styles.days__day_selected;
        }
        return (
          <div
            key={day.dayNumber}
            onClick={() => dayClickHandler(day)}
            className={`${styles.days__day} ${dayClassName || ''}`}
          >
            {day.date.getDate()}
          </div>
        );
      });
    };

    return (
      <>
        <div className={styles.calendar__container}>
          <div className={styles.container__content}>
            <div className={styles.content__legend}>
              <button
                className={styles.content__legend_button}
                onClick={() => setIsLegendOpen(true)}
              >
                ?
              </button>
            </div>
            <div className={styles.content__month}>
              <div
                className={styles.month_selector}
                onClick={() => {
                  dayCalendarStore.setSelectedDate({
                    year: newDate.getFullYear(),
                    month: newDate.getMonth(),
                    day: newDate.getDate(),
                  });
                  setIsMonthCalendarOpen(true);
                }}
              >
                <span>
                  {newDate.getDate()} {MonthNamesNum[month]}
                </span>
                <div className={styles.month_selector__triangle}>
                  <RoundTriangle isRotated={isMonthCalendarOpen} />
                </div>
              </div>
            </div>
            <div className={styles.content__today}>
              <button className={styles.today__button} onClick={goToToday}>
                Сегодня
              </button>
            </div>
            <div className={styles.content__microslot}>
              <div
                ref={microSlotRef}
                className={`${styles.microslot__custom} ${isMicroSlotOpen ? styles.microslot__custom_open : ''} ${isMobile ? styles.microslot__custom_upward : ''}`}
                onClick={() => setIsMicroSlotOpen(!isMicroSlotOpen)}
              >
                <div className={styles.microslot__header}>
                  {microSlotDuration} мин
                  <div className={styles.microslot__triangle}>
                    <RoundTriangle isRotated={!isMicroSlotOpen} />
                  </div>
                </div>
                {isMicroSlotOpen && (
                  <div className={styles.microslot__options}>
                    {[5, 10, 15].map((duration) => (
                      <div
                        key={duration}
                        className={`${styles.microslot__option} ${duration === microSlotDuration ? styles.microslot__option_selected : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMicroSlotDuration(duration);
                          setIsMicroSlotOpen(false);
                        }}
                      >
                        {duration} мин
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.content__week}>
              <div className={styles.week__names}>
                {WeekDaysArray.map((name, index) => (
                  <div key={index} className={styles.names__block}>
                    {name}
                  </div>
                ))}
              </div>
              <div className={styles.week__slider}>
                <div
                  className={styles.slider__arrow}
                  onClick={() => handleArrowClick(-1)}
                >
                  <LeftArrowSvg />
                </div>
                <div
                  ref={containerRef}
                  className={`${styles.slider__days} ${styles.slider__days_draggable}`}
                  onMouseDown={handleTouchStart}
                  onMouseMove={handleTouchMove}
                  onMouseUp={handleTouchEnd}
                  onMouseLeave={handleTouchEnd}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div
                    ref={trackRef}
                    className={styles.carousel__track}
                    onTransitionEnd={handleTransitionEnd}
                  >
                    <div
                      className={styles.carousel__slide}
                      style={{
                        position: 'absolute',
                        left: 0,
                        transform: !animationDirection
                          ? 'translateX(-100%)'
                          : animationDirection === 'left'
                            ? 'translateX(0)'
                            : 'translateX(-100%)',
                        opacity: animationDirection === 'left' ? 1 : 0,
                        transition:
                          animationDirection !== null
                            ? 'transform 0.3s ease-out, opacity 0.3s'
                            : 'none',
                        zIndex: 1,
                      }}
                    >
                      {prevWeek
                        ? renderWeekDays(prevWeek)
                        : renderWeekDays(currentWeek)}
                    </div>
                    <div
                      className={styles.carousel__slide}
                      style={{
                        position: 'relative',
                        transform:
                          animationDirection === 'left'
                            ? 'translateX(100%)'
                            : animationDirection === 'right'
                              ? 'translateX(-100%)'
                              : 'translateX(0)',
                        transition:
                          animationDirection !== null
                            ? 'transform 0.3s ease-out'
                            : 'none',
                        zIndex: 2,
                      }}
                    >
                      {currentWeek
                        ? renderWeekDays(currentWeek)
                        : renderWeekDays(week)}
                    </div>
                    <div
                      className={styles.carousel__slide}
                      style={{
                        position: 'absolute',
                        right: 0,
                        transform: !animationDirection
                          ? 'translateX(100%)'
                          : animationDirection === 'right'
                            ? 'translateX(0)'
                            : 'translateX(100%)',
                        opacity: animationDirection === 'right' ? 1 : 0,
                        transition:
                          animationDirection !== null
                            ? 'transform 0.3s ease-out, opacity 0.3s'
                            : 'none',
                        zIndex: 1,
                      }}
                    >
                      {nextWeek
                        ? renderWeekDays(nextWeek)
                        : renderWeekDays(currentWeek)}
                    </div>
                  </div>
                </div>
                <div
                  className={styles.slider__arrow}
                  onClick={() => handleArrowClick(1)}
                >
                  <RightArrowSvg />
                </div>
              </div>
            </div>
          </div>
        </div>
        {isMonthCalendarOpen && (
          <ModalWindow onClickOutside={() => setIsMonthCalendarOpen(false)}>
            <div className={styles.calendar_popup}>
              <MiniCalendar
                selectedDateHandler={(date) => {
                  const newSelectedDate = new Date(
                    date.year,
                    date.month,
                    date.day
                  );
                  setNewDate(newSelectedDate);
                  setMonth(date.month);
                  dateChangeHandler(newSelectedDate);
                  setIsMonthCalendarOpen(false);
                }}
                shouldNavigate={false}
                compact
              />
            </div>
          </ModalWindow>
        )}
        {isLegendOpen && (
          <ModalWindow onClickOutside={() => setIsLegendOpen(false)}>
            <div className={styles.legend_modal}>
              <h2 className={styles.legend_modal__title}>Описание цветов</h2>

              <div className={styles.legend_modal__section}>
                <h3 className={styles.legend_modal__section_title}>Записи</h3>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_booking}`}
                  />
                  <span>Обычная запись</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_self_service}`}
                  />
                  <span>Самостоятельная запись клиента</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_overlap}`}
                  />
                  <span>Пересекающаяся запись</span>
                </div>
              </div>

              <div className={styles.legend_modal__section}>
                <h3 className={styles.legend_modal__section_title}>Перерывы</h3>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_break}`}
                  />
                  <span>Перерыв сотрудника</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_break_overlap}`}
                  />
                  <span>Пересекающийся перерыв</span>
                </div>
              </div>

              <div className={styles.legend_modal__section}>
                <h3 className={styles.legend_modal__section_title}>
                  Типы дней
                </h3>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_workday}`}
                  />
                  <span>Рабочий день</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_dayoff}`}
                  />
                  <span>Выходной</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_vacation}`}
                  />
                  <span>Отпуск</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_administrative}`}
                  />
                  <span>Административный</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_sick_leave}`}
                  />
                  <span>Больничный</span>
                </div>
                <div className={styles.legend_modal__item}>
                  <div
                    className={`${styles.legend_modal__color} ${styles.legend_modal__color_outside}`}
                  />
                  <span>Вне рабочего времени</span>
                </div>
              </div>
            </div>
          </ModalWindow>
        )}
      </>
    );
  }
);
